import { useEffect, useState } from "react";
import { Star } from "lucide-react";
import FlowerButton from "@/components/FlowerButton";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtDate = (iso) => new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });

export const Stars = ({ value, size = "w-4 h-4", testId }) => (
  <span className="inline-flex gap-0.5" aria-label={`${value} sur 5`} data-testid={testId}>
    {[1, 2, 3, 4, 5].map((n) => <Star key={n} className={`${size} ${n <= value ? "fill-[#F2C94C] text-[#F2C94C]" : "text-[#D1D5CB]"}`} />)}
  </span>
);

const StarPicker = ({ value, onChange }) => {
  const [hover, setHover] = useState(0);
  return (
    <div className="flex gap-1" data-testid="review-stars">
      {[1, 2, 3, 4, 5].map((n) => (
        <button key={n} type="button" onClick={() => onChange(n)} onMouseEnter={() => setHover(n)} onMouseLeave={() => setHover(0)} aria-label={`${n} étoile${n > 1 ? "s" : ""}`} data-testid={`review-star-${n}`} className="p-0.5">
          <Star className={`w-8 h-8 transition-transform hover:scale-110 ${n <= (hover || value) ? "fill-[#F2C94C] text-[#F2C94C]" : "text-[#D1D5CB]"}`} />
        </button>
      ))}
    </div>
  );
};

export const Reviews = ({ productKey, productName }) => {
  const [data, setData] = useState({ reviews: [], count: 0, average: null });
  const [form, setForm] = useState({ name: "", rating: 5, message: "", website: "" });
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = () => fetch(`${API}/reviews?product=${encodeURIComponent(productKey)}`).then((r) => r.json()).then(setData).catch(() => {});
  useEffect(() => { load(); }, [productKey]); // eslint-disable-line

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || form.message.trim().length < 3) return toast.error("Un prénom et un petit mot, s'il te plaît ✿");
    setBusy(true);
    try {
      const res = await fetch(`${API}/reviews`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ ...form, product_key: productKey }) });
      if (!res.ok) throw new Error();
      toast.success("Merci pour ton mot doux ✿");
      setForm({ name: "", rating: 5, message: "", website: "" }); setOpen(false); load();
    } catch { toast.error("Impossible d'envoyer ton avis, réessaie dans un instant"); }
    finally { setBusy(false); }
  };

  return (
    <section className="mt-14 rounded-[2rem] border-2 border-dashed border-[#D1D5CB] bg-[#FAF9F6] p-6 md:p-10" data-testid="reviews-section">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="font-cursive text-2xl text-[#78353A]/70">Les mots doux</p>
          <h2 className="font-cursive text-4xl md:text-5xl text-[#78353A] leading-none">Ce qu'en pensent les potes</h2>
          {data.count > 0 ? (
            <p className="mt-2 flex items-center gap-2 text-sm text-[#4A3B32]/80" data-testid="reviews-summary">
              <Stars value={Math.round(data.average)} /> <span className="font-semibold">{String(data.average).replace(".", ",")}/5</span> · {data.count} avis
            </p>
          ) : (
            <p className="mt-2 text-sm text-[#4A3B32]/70" data-testid="reviews-empty">Pas encore d'avis — sois le premier à laisser un mot doux ✿</p>
          )}
        </div>
        {!open && <FlowerButton variant="pink" onClick={() => setOpen(true)} data-testid="review-open-btn" className="!py-3 !px-5 !text-lg">Laisser un mot doux</FlowerButton>}
      </div>

      {open && (
        <form onSubmit={submit} className="mt-6 rounded-3xl bg-[#FFFDF9] border-2 border-dashed border-[#D1D5CB] p-5 grid gap-4" data-testid="review-form">
          <p className="text-sm text-[#4A3B32]/80">Ton avis sur <span className="font-semibold">{productName}</span></p>
          <StarPicker value={form.rating} onChange={(rating) => setForm({ ...form, rating })} />
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} maxLength={40} placeholder="Ton prénom" data-testid="review-name-input"
            className="rounded-full border-2 border-dashed border-[#D1D5CB] bg-[#FAF9F6] px-4 py-2.5 text-sm text-[#4A3B32] focus:outline-none focus:border-[#78353A]" />
          <textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} maxLength={600} rows={3} placeholder="Ton petit mot…" data-testid="review-message-input"
            className="rounded-2xl border-2 border-dashed border-[#D1D5CB] bg-[#FAF9F6] px-4 py-2.5 text-sm text-[#4A3B32] focus:outline-none focus:border-[#78353A] resize-y" />
          <input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
          <div className="flex flex-wrap items-center gap-3">
            <FlowerButton type="submit" variant="pink" disabled={busy} data-testid="review-submit-btn" className="!py-3 !px-5 !text-lg">{busy ? "…" : "Envoyer"}</FlowerButton>
            <button type="button" onClick={() => setOpen(false)} className="text-sm text-[#78353A] underline decoration-dotted underline-offset-4" data-testid="review-cancel-btn">Annuler</button>
          </div>
        </form>
      )}

      {data.reviews.length > 0 && (
        <ul className="mt-8 grid sm:grid-cols-2 gap-4" data-testid="reviews-list">
          {data.reviews.map((r) => (
            <li key={r.id} className="rounded-3xl bg-[#FFFDF9] border-2 border-dashed border-[#D1D5CB] p-5" data-testid={`review-${r.id}`}>
              <div className="flex items-center justify-between gap-3">
                <Stars value={r.rating} />
                <span className="text-xs text-[#4A3B32]/55">{fmtDate(r.created_at)}</span>
              </div>
              <p className="mt-3 text-sm leading-relaxed text-[#4A3B32]/90">« {r.message} »</p>
              <p className="mt-2 font-cursive text-xl text-[#78353A]">— {r.name}</p>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};
